import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Drawer, Grid, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, useTheme } from '@mui/material'
import { Menu, Close } from '@mui/icons-material'
/* import logo from '../../assets/Logologo_icv_1.svg'; */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSync, faHome, faInfoCircle, faSignOutAlt, faClipboardList, faUserCog, faListAlt, faTruck, faUser, faCalendar, faBook } from '@fortawesome/free-solid-svg-icons'
import { useAuth, useNavigation, useReportsContext, useUser, useExecutionReportContext } from '../../context'
import { useStatus } from '../../context/Status.context'
import { manualsRoutes } from '../../routes'
import { InternalMessageModal, VersionControlModal } from '../../modals'
import { useLoadingContext } from '../../context/Loading.context'
import { ROLES } from '../../config'
import packageJson from '../../../package.json'
import ReportsResumeDialog from '../../dialogs/ReportsResumeDialog'
import UserManualsDialog from '../../dialogs/UserManualsDialog'

const Navbar = () => {
    const theme = useTheme()
    const { logout } = useAuth()
    const {
        admin,
        isOperator,
        isUniversalInspector,
        isUniversalChief,
        isOperatorInspector,
        isSapExecutive,
        isShiftManager,
        isChiefMachinery, permisos,
        userManual,
        userManuals = []
    } = useUser()
    const { setStatus } = useStatus()
    const { reports, getReports, pautas, terminarJornada, checkForOpenReportAndLogout, initiateEndAllShifts } = useReportsContext()
    const [openReportsResume, setOpenReportsResume] = useState(false)
    const [openManualsDialog, setOpenManualsDialog] = useState(false)
    const { navBarOpen, handleNavBar, closeNavBar } = useNavigation()
    const { syncQueue } = useExecutionReportContext()
    const [path, setPath] = useState('')
    const location = useLocation()

    useEffect(() => {
        closeNavBar()
    }, [])

    const [disableButtonNoAdmin, setDisableButtonsNoAdmin] = useState(true)
    const [disabled, setDisabled] = useState(true)
    const [openVersionModal, setOpenVersionModal] = useState(false)
    const [openInternalMessagesModal, setOpenInternalMessagesModal] = useState(false)
    const { setLoadingLogo } = useLoadingContext()

    const [listaNavbar, setListaNavbar] = useState([])

    const navigate = useNavigate()

    const handleDownloadManual = async () => {
        if (!userManual || !userManual._id) return
        setStatus({
            status: 'loading',
            title: 'Manual de Uso',
            message: 'Obteniendo enlace seguro de descarga...'
        })
        try {
            const res = await manualsRoutes.getManualDownloadUrl(userManual._id)
            if (res && res.data && res.data.url) {
                window.open(res.data.url, '_blank')
                setStatus({
                    status: 'completed',
                    title: 'Descarga Iniciada',
                    message: `El manual "${res.data.title || 'Manual de Uso'}" se ha descargado correctamente.`,
                    severity: 'success'
                })
            } else {
                throw new Error('No se pudo obtener el enlace de descarga.')
            }
        } catch (error) {
            console.error('Error downloading manual:', error)
            setStatus({
                status: 'error',
                title: 'Error de Descarga',
                message: error.response?.data?.message || error.message || 'No se pudo descargar el manual de uso.',
                severity: 'error'
            })
        }
    }

    const shouldShowItem = (itemName) => {
        if (itemName === 'Manual de Uso') {
            return (userManuals && userManuals.length > 0) || !!userManual
        }

        if (admin) return true

        const isRegularOperator = isOperator && !isUniversalInspector && !isOperatorInspector && !isUniversalChief

        if (isRegularOperator) {
            return ['Inicio', 'Asignaciones', 'Perfil', 'Sincronizar', 'Cerrar sesión', 'Calendario', 'Manual de Uso'].includes(itemName)
        }

        // Executive / universal inspector / operator inspector / observer
        const isSpecialUserForAssignments = isUniversalInspector || isOperatorInspector

        if (itemName === 'Asignaciones') {
            return isSpecialUserForAssignments
        }

        if (itemName === 'Administración') {
            return false
        }

        return ['Inicio', 'Calendario', 'Máquinas', 'Reportes', 'Perfil', 'Información', 'Sincronizar', 'Cerrar sesión', 'Manual de Uso'].includes(itemName)
    }

    const list = [
        {
            name: 'Inicio',
            path: '/welcome',
            icon: faHome,
            permisos: [],
            respuestaPermiso: true
        },
        {
            name: 'Administración',
            path: '/administration',
            icon: faUserCog,
            permisos: [ROLES.ADMIN],
            respuestaPermiso: true
        },
        {
            name: 'Calendario',
            path: '/calendar',
            icon: faCalendar,
            permisos: [],
            respuestaPermiso: true
        },
        {
            name: 'Asignaciones',
            path: '/assignment',
            icon: faListAlt,
            permisos: [],
            respuestaPermiso: true
        },
        {
            name: 'Máquinas',
            path: '/machines',
            icon: faTruck,
            permisos: [],
            hideWhenOperator: true, // This will be used to disable the button for operators
            respuestaPermiso: true
        },
        {
            name: 'Reportes',
            path: '/reports',
            icon: faClipboardList,
            permisos: [ROLES.ADMIN, ROLES.SAP_EXECUTIVE, ROLES.CHIEF_MACHINERY, ROLES.SHIFT_MANAGER, ROLES.OPERATOR_INSPECTOR_UNIVERSAL],
            respuestaPermiso: true
        },
        {
            name: 'Perfil',
            path: '/user-profile',
            icon: faUser,
            permisos: [],
            respuestaPermiso: true
        },
        {
            name: 'Información',
            path: 'info',
            icon: faInfoCircle,
            permisos: [],
            respuestaPermiso: true
        },
        {
            name: 'Sincronizar',
            path: 'sync',
            icon: faSync,
            permisos: [],
            respuestaPermiso: true
        },
        {
            name: 'Manual de Uso',
            path: 'manual',
            icon: faBook,
            permisos: [],
            respuestaPermiso: true
        }
    ]

    useEffect(() => {
        revisarLista()
    }, [permisos, isOperator, isUniversalInspector, isUniversalChief])

    const revisarLista = () => {
        const updatedList = list.map(item => {
            let disabled = false

            if (item.hideWhenOperator && isOperator && isUniversalInspector) {
                disabled = true
            }

            if (!disabled && item.permisos.length > 0) {
                const hasPermission = item.permisos.some(requiredPermiso => permisos.includes(requiredPermiso))
                if (!hasPermission) {
                    disabled = true
                }
            }
            return { ...item, respuestaPermiso: disabled }
        })
        setListaNavbar(updatedList)
    }

    const closeSideBar = () => {
        if (navBarOpen) {
            handleNavBar()
        }
    }

    const toOpenVersionModal = () => {
        setOpenVersionModal(true)
    }
    const closeModal = () => {
        setOpenVersionModal(false)
    }

    const toOpenInternalMessagesModal = () => {
        setOpenInternalMessagesModal(true)
    }
    const closeInternalMessageModal = () => {
        setOpenInternalMessagesModal(false)
    }

    const isMobile = {
        Android: function () {
            return navigator.userAgent.match(/Android/i)
        },
        BlackBerry: function () {
            return navigator.userAgent.match(/BlackBerry/i)
        },
        iOS: function () {
            return navigator.userAgent.match(/iPhone|iPad|iPod/i)
        },
        Opera: function () {
            return navigator.userAgent.match(/Opera Mini/i)
        },
        Windows: function () {
            return navigator.userAgent.match(/IEMobile/i) || navigator.userAgent.match(/WPDesktop/i)
        },
        any: function () {
            return (isMobile.Android() || isMobile.BlackBerry() || isMobile.iOS() || isMobile.Opera() || isMobile.Windows())
        },
        other: function () {
            return navigator.userAgent.match(/Mozilla/i) || navigator.userAgent.match(/AppleWebKit/i) || navigator.userAgent.match(/Chrome/i) || navigator.userAgent.match(/Safari/i)
        }
    }

    const openCadAssistant = () => {
        if (isMobile.Android()) {
            window.location.replace('market://details?id=org.opencascade.cadassistant')
        } else {
            alert('Su dispositivo no cuenta con el link a Cad Assistant')
        }
    }

    useEffect(() => {
        if (admin) {
            setDisableButtonsNoAdmin(false)
        } else {
            setDisableButtonsNoAdmin(true)
        }
        if (admin || isSapExecutive || isShiftManager || isChiefMachinery) {
            setDisabled(false)
        } else {
            setDisabled(true)
        }
    }, [admin, isSapExecutive, isShiftManager, isChiefMachinery])

    useEffect(() => {
        if (location) {
            setPath(location.pathname)
        }
    }, [location])

    const revisePermisos = async (item) => {
        let permisoData = true
        const esperaPermiso = await Promise.all([admin, isOperator, isUniversalInspector, isSapExecutive, isChiefMachinery, isShiftManager].map((pos) => {
            return item.permisos.map((permiso) => {
                if (pos === permiso) {
                    return false
                }
                return null
            })
        }))
        permisoData = esperaPermiso
        console.log(permisoData)
        return permisoData
    }

    const handleLogoutCheck = () => {
        if (syncQueue && syncQueue.length > 0) {
            alert(`No puede cerrar sesión mientras haya sincronizaciones pendientes en la cola (${syncQueue.length} restante(s)). Por favor, espere a que finalice la sincronización o a que se recupere la conexión para evitar pérdida de datos.`)
            return
        }
        checkForOpenReportAndLogout()
    }

    return (
        <div>
            <Drawer
                anchor='left'
                open={navBarOpen}
                onClose={handleNavBar}
                PaperProps={{
                    sx: {
                        marginTop: 0,
                        backgroundColor: '#212121',
                        border: 'none',
                        borderEndEndRadius: 0,
                        borderTopRightRadius: 0,
                        transition: theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: navBarOpen ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen
                        }),
                        width: navBarOpen ? 230 : 99,
                        maxWidth: navBarOpen ? '100vw' : 99,
                        whiteSpace: 'nowrap',
                        overflowX: 'hidden',
                        height: '100%'
                    }
                }}
                sx={{
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    width: navBarOpen ? 230 : 99,
                    maxWidth: navBarOpen ? '100vw' : 99,
                    overflowX: 'hidden',
                    transition: (theme) => theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: navBarOpen ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen
                    })
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        boxSizing: 'border-box'
                    }}
                >
                    <div style={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                        <Grid container spacing={2} sx={{ padding: navBarOpen ? 2 : '8px 0 0 0' }}>
                            <Grid item xs={12} container>
                                <List>
                                    {
                                        listaNavbar
                                            .filter(item => shouldShowItem(item.name))
                                            .map((item, i) => {
                                                return (
                                                    <ListItem key={i} disablePadding style={{ color: 'white' }}>
                                                        <ListItemButton
                                                            onClick={() => {
                                                                if (item.path === 'info') {
                                                                    setOpenReportsResume(true)
                                                                } else if (item.path === 'sync') {
                                                                    setLoadingLogo(true)
                                                                    getReports()
                                                                } else if (item.path === 'manual') {
                                                                    setOpenManualsDialog(true)
                                                                } else {
                                                                    navigate(item.path)
                                                                }
                                                                closeNavBar()
                                                            }}>
                                                            <ListItemIcon>
                                                                <FontAwesomeIcon color='white' icon={item.icon} />
                                                            </ListItemIcon>
                                                            <ListItemText primary={item.name} />
                                                        </ListItemButton>
                                                    </ListItem>
                                                )
                                            })
                                    }
                                    {/* {isOperator && (
                                    <ListItem style={{ color: 'white' }}>
                                        <ListItemButton onClick={() => initiateEndAllShifts()}>
                                            <ListItemIcon>
                                                <FontAwesomeIcon color='white' icon={faClipboardList} />
                                            </ListItemIcon>
                                            <ListItemText primary={'Terminar Jornadas'} />
                                        </ListItemButton>
                                    </ListItem>
                                )} */}
                                    <ListItem disablePadding style={{ color: 'white' }}>
                                        <ListItemButton onClick={() => {
                                            console.log('Logout clicked')
                                            handleLogoutCheck()
                                        }}>
                                            <ListItemIcon>
                                                <FontAwesomeIcon color='white' icon={faSignOutAlt} />
                                            </ListItemIcon>
                                            <ListItemText primary={'Cerrar Sesión'} />
                                        </ListItemButton>
                                    </ListItem>
                                </List>
                                {/* <div style={{width: '100%', textAlign: navBarOpen ? 'right' : 'center'}}>
                                <IconButton title={!navBarOpen ? 'Abrir Menú' : 'Cerrar Menú'} onClick={handleNavBar} className={navBarOpen ? classes.sideButtonsOpenMenu : classes.sideButtonsCloseMenu} >
                                    {navBarOpen ? <Close /> : <Menu />}
                                </IconButton>
                            </div>
                            {navBarOpen &&
                                <div style={{padding: 10}}>
                                    <img height={60} src={'./assets/Logologo_icv_1.svg'} alt="" />
                                    <p style={{color: '#fff', margin: 0}}>Ingeniería Civil Vicente</p>
                                </div>
                            }
                            <div style={{height: 1, backgroundColor: '#fff', width: '100%', marginRight: 10, marginLeft: 10}}>

                            </div>
                            <div style={{width: '100%', marginTop: 20, textAlign: navBarOpen ? 'left' : 'center'}}>
                                <IconButton style={{padding: 9}} onClick={closeSideBar} title='Inicio'>
                                    <Link to='/welcome' className={classes.sideButtons} style={{ textDecoration: 'none', color: (path === '/welcome') ? '#BE2E26' : '#FFFFFF' }}>
                                        <FontAwesomeIcon icon={faHome}/> {navBarOpen ?  ' Inicio' : ''}
                                    </Link>
                                </IconButton>
                            </div>
                            <div style={{width: '100%', textAlign: navBarOpen ? 'left' : 'center'}}>
                                <IconButton style={{padding: 9}} onClick={closeSideBar} title='Calendario'>
                                    <Link to='/calendar' className={classes.sideButtons} style={{ textDecoration: 'none', color: (path === '/calendar') ? '#BE2E26' : '#FFFFFF' }}>
                                        <FontAwesomeIcon icon={faCalendar}/> {navBarOpen ?  ' Calendario' : ''}
                                    </Link>
                                </IconButton>
                            </div>
                            <div style={{width: '100%', textAlign: navBarOpen ? 'left' : 'center'}}>
                                <IconButton style={{padding: 9}} onClick={closeSideBar} title='Listado Asignaciones'>
                                    <Link to='/assignment' className={classes.sideButtons} style={{ color: (path.includes('/assignment')) ? '#BE2E26' : '#FFFFFF', textDecoration: 'none' }}>
                                        <FontAwesomeIcon icon={faListAlt}/> {navBarOpen ?  ' Listado Asignaciones' : ''}
                                    </Link>
                                </IconButton>
                            </div>
                            <div style={{width: '100%', textAlign: navBarOpen ? 'left' : 'center'}}>
                                <IconButton style={{padding: 9}} onClick={closeSideBar} title='Máquinas'>
                                    <Link to='/faTruck' className={classes.sideButtons} style={{ textDecoration: 'none', color: (path.match('/machines')) ? '#BE2E26' : '#FFFFFF' }}>
                                        <FontAwesomeIcon icon={faTruck}/> {navBarOpen ?  ' Máquinas' : ''}
                                    </Link>
                                </IconButton>
                            </div>
                            {!disableButtonNoAdmin && <div style={{width: '100%', textAlign: navBarOpen ? 'left' : 'center'}}>
                                <IconButton style={{padding: 9}} onClick={closeSideBar} title='Obras'>
                                    <Link to='/sites' className={classes.sideButtons} style={{ color: (path === '/sites') ? '#BE2E26' : '#FFFFFF', textDecoration: 'none' }}>
                                        <FontAwesomeIcon icon={faMapMarkerAlt}/> {navBarOpen ?  ' Obras' : ''}
                                    </Link>
                                </IconButton>
                            </div>}
                            {!disabled && <div style={{width: '100%', textAlign: navBarOpen ? 'left' : 'center'}}>
                                <IconButton style={{padding: 9}} onClick={closeSideBar} title='Ordenes de Trabajo'>
                                    <Link to='/reports' className={classes.sideButtons} style={{ color: (path.includes('/reports')) ? '#BE2E26' : '#FFFFFF', textDecoration: 'none' }}>
                                        <FontAwesomeIcon icon={faClipboardList}/> {navBarOpen ?  ' Ordenes de Trabajo' : ''}
                                    </Link>
                                </IconButton>
                            </div>}
                            {!disableButtonNoAdmin && <div style={{width: '100%', textAlign: navBarOpen ? 'left' : 'center'}}>
                                <IconButton style={{padding: 9}} onClick={closeSideBar} title='Administración'>
                                    <Link to='/administration' className={classes.sideButtons} style={{ color: (path.includes('/administration')||path.includes('/users')||path.includes('/edit-user')||path.includes('/new-users')||path.includes('/patterns')) ? '#BE2E26' : '#FFFFFF', textDecoration: 'none' }}>
                                        <FontAwesomeIcon icon={faUserCog}/> {navBarOpen ?  ' Administración' : ''}
                                    </Link>
                                </IconButton>
                            </div>}
                            <div style={{width: '100%', textAlign: navBarOpen ? 'left' : 'center'}}>
                                <IconButton style={{padding: 9}} onClick={closeSideBar} title='Perfil'>
                                    <Link to='/user-profile' className={classes.sideButtons} style={{ color: (path.includes('/user-profile')) ? '#BE2E26' : '#FFFFFF', textDecoration: 'none' }}>
                                        <FontAwesomeIcon icon={faUser}/> {navBarOpen ?  ' Perfil' : ''}
                                    </Link>
                                </IconButton>
                            </div>
                            <div style={{width: '100%', textAlign: navBarOpen ? 'left' : 'center'}}>
                                <IconButton style={{padding: 9}} onClick={closeSideBar} title='Actualizar' onClickCapture={()=>{getReports()}}>
                                    <div className={classes.sideButtons} style={{ color: '#FFFFFF', textDecoration: 'none' }}>
                                        <FontAwesomeIcon icon={faSync}/> {navBarOpen ?  ' Actualizar' : ''}
                                    </div>
                                </IconButton>
                            </div>
                            <div style={{width: '100%', textAlign: navBarOpen ? 'left' : 'center'}}>
                                <IconButton style={{padding: 9}} onClick={closeSideBar} title='Int. Artificial' onClickCapture={()=>{toOpenIAModal()}}>
                                    <div className={classes.sideButtons} style={{ color: (path === '/pms') ? '#BE2E26' : '#FFFFFF', textDecoration: 'none' }}>
                                        <FontAwesomeIcon icon={faRobot}/> {navBarOpen ?  ' Int. Artificial' : ''}
                                    </div>
                                </IconButton>
                            </div>
                            <div style={{width: '100%', textAlign: navBarOpen ? 'left' : 'center'}}>
                                <IconButton style={{padding: 9}} onClick={closeSideBar} title='Mensajes App' onClickCapture={()=>{toOpenInternalMessagesModal()}}>
                                    <div className={classes.sideButtons} style={{ color: (path === '/pms') ? '#BE2E26' : '#FFFFFF', textDecoration: 'none' }}>
                                        <FontAwesomeIcon icon={faComment}/> {navBarOpen ?  ' Mensajes App' : ''}
                                    </div>
                                </IconButton>
                            </div> */}
                                {/* <div style={{width: '100%', textAlign: navBarOpen ? 'left' : 'center'}}>
                                <IconButton style={{padding: 9}} onClick={closeSideBar}  title='Información' onClickCapture={()=>{toOpenVersionModal()}}>
                                    <div className={classes.sideButtons} style={{ color: (path === '/information') ? '#BE2E26' : '#FFFFFF', textDecoration: 'none' }}>
                                        <FontAwesomeIcon icon={faInfoCircle}/> {navBarOpen ?  ' Información' : ''}
                                    </div>
                                </IconButton>
                            </div> */}
                                {/* <div style={{width: '100%', textAlign: navBarOpen ? 'left' : 'center'}}>
                                <IconButton style={{padding: 9}} onClick={closeSideBar}  title='Información' onClickCapture={()=>{openCadAssistant()}}>
                                    <div className={classes.sideButtons} style={{ color: (path === '/information') ? '#BE2E26' : '#FFFFFF', textDecoration: 'none' }}>
                                        <FontAwesomeIcon icon={faCube}/> {navBarOpen ?  ' Cad Assistant' : ''}
                                    </div>
                                </IconButton>
                            </div> */}
                                {/* <div style={{width: '100%', textAlign: navBarOpen ? 'left' : 'center'}}>
                                <IconButton style={{padding: 9}} onClick={closeSideBar}  title='Opciones'>
                                    <Link to={'/options'} className={classes.sideButtons} style={{ color: (path === '/options') ? '#BE2E26' : '#FFFFFF', textDecoration: 'none' }}>
                                        <FontAwesomeIcon icon={faSlidersH}/> {navBarOpen ?  ' Opciones' : ''}
                                    </Link>
                                </IconButton>
                            </div>
                            <div style={{width: '100%', textAlign: navBarOpen ? 'left' : 'center',}}>
                                <IconButton style={{padding: 9}} onClick={logout} title='Cerrar Sesión'>
                                    <div className={classes.sideButtons}>
                                        <FontAwesomeIcon icon={faSignOutAlt}/> {navBarOpen ?  ' Cerrar Sesión' : ''}
                                    </div>
                                </IconButton>
                            </div> */}
                            </Grid>
                        </Grid>
                    </div>
                    {openVersionModal && <VersionControlModal open={openVersionModal} closeModal={closeModal} />}
                    {openInternalMessagesModal && <InternalMessageModal open={openInternalMessagesModal} closeModal={closeInternalMessageModal} />}

                    <div
                        style={{
                            padding: '20px 0',
                            textAlign: 'center',
                            color: '#757575',
                            flexShrink: 0,
                            pointerEvents: 'none'
                        }}
                    >
                        <span style={{ fontSize: '11px' }}>
                            {navBarOpen ? `Versión ${packageJson.version}` : `v${packageJson.version}`}
                        </span>
                    </div>
                </div>
            </Drawer>
            {openReportsResume && <ReportsResumeDialog open={openReportsResume} reports={reports} handleClose={() => setOpenReportsResume(false)} />}
            {openManualsDialog && (
                <UserManualsDialog
                    open={openManualsDialog}
                    handleClose={() => setOpenManualsDialog(false)}
                    manuals={(userManuals && userManuals.length > 0) ? userManuals : (userManual ? [userManual] : [])}
                />
            )}
        </div>
    )
}

export default Navbar
